import { createSelector } from "@reduxjs/toolkit";
import { AuthSelector, MasterSelector } from ".";
import unionBy from "lodash/unionBy";
import { GenderOption, TitleOption } from "const";
import { mapAccountsToSelectOptions } from "utils/misc/account-util";

export const CustomerSelector = {
  state: (state) => state.customer,
  loading: (state) => CustomerSelector.state(state).loading,
  error: (state) => CustomerSelector.state(state).error,
  isUhnwUser: (state) => CustomerSelector.state(state).isUhnwUser,
  wealthFlag: (state) => CustomerSelector.state(state).wealthFlag,
  specialFlag: (state) => CustomerSelector.state(state).specialFlag,
  isPB: (state) => CustomerSelector.state(state).isPB,
  isPPB: (state) => CustomerSelector.state(state).isPPB,
  isPwmUser: (state) => CustomerSelector.state(state).isPwmUser,
  isFinalPwmUser: (state) => CustomerSelector.state(state).isFinalPwmUser,
  fetchUserTypeOnce: (state) => CustomerSelector.state(state).fetchUserTypeOnce,
  registered: (state) => CustomerSelector.state(state).registered,
  suitability: (state) => CustomerSelector.state(state).suitability,
  riskResult: (state) => CustomerSelector.state(state).riskResult,
  checkingUserType: (state) => CustomerSelector.state(state).checkingUserType,
  pwmRegisteringStatus: (state) => CustomerSelector.state(state).pwmRegisteringStatus ?? {},
  listAssetAllocation: (state) => CustomerSelector.state(state).listAssetAllocation,
  isMuleAccount: (state) => CustomerSelector.state(state).muleAccount,
  registerAndSuitabilityAreValid: (state) => CustomerSelector.registered(state) && CustomerSelector.suitability(state),
  userInfo: createSelector(
    [(state) => CustomerSelector.state(state).userInfo, (state) => AuthSelector.username(state)],
    (userInfo, username) => ({ ...userInfo, username })
  ),
  titleOptions: createSelector(
    (state) => MasterSelector.isTitleOthersLangTh(state),
    (isLocaleTH) => (isLocaleTH ? TitleOption.TH : TitleOption.EN)
  ),
  genderOptions: createSelector(
    (state) => MasterSelector.isLocaleTH(state),
    // (state) => MasterSelector.isTitleOthersLangTh(state),
    (isLocaleTH) => (isLocaleTH ? GenderOption.TH : GenderOption.EN)
  ),
  uhmfLoading: (state) => CustomerSelector.state(state).uhmf.loading,
  uhmfList: (state) => CustomerSelector.state(state).uhmf.list,
  fetchedUhmf: (state) => CustomerSelector.state(state).uhmf.done,
  bankruptcyPassed: (state) => CustomerSelector.state(state).bankruptcyPassed,

  //* Accounts
  //* Normal Account usually use at Primary Market
  accounts: createSelector(
    [(state) => CustomerSelector.state(state).accounts, (state) => CustomerSelector.state(state).additionalAccounts],
    (accounts, additionalAccounts) => {
      //* example account data ::
      // {
      //   accountNo: 'WEnuCenbIxsKYErlMQOh',
      //   accountName: 'นาย Monney12 Connect12',
      //   accountNoDisplay: 'XXX-X-XX566-0',
      //   accountStatus: 0,
      //   productType: 2001,
      //   brnOwnerShip: 0,
      //   balAmt: 1419383576.84,
      //   availBalAmt: 1418883576.84,
      //   productGroup: 'SAV',
      //   currCd: 'THB',
      //   sfcdAccountId: null,
      //   payment: true,
      //   receive: true,
      //   topupDeepLinkUrl: null
      // }

      //* example additional account data ::
      // {
      //   accountNo: 'WEnuCenbIxsKYErlMQOh',
      //   accountNoDisplay: 'XXX-X-XX566-0',
      // }

      // If accountNoDisplay of additionalAccounts matches in accounts, merge data from accounts as well
      // Build a map from accountNoDisplay to account object in accounts
      const accountNoDisplayMap = {};
      (accounts || []).forEach(acc => {
        if (acc.accountNoDisplay) {
          accountNoDisplayMap[acc.accountNoDisplay] = acc;
        }
      });

      const mergedAdditionalAccounts = (additionalAccounts || []).map(addAcc => {
        // If accountNoDisplay matches one in accounts, merge data from accounts, prefer fields from accounts
        if (addAcc.accountNoDisplay && accountNoDisplayMap[addAcc.accountNoDisplay]) {
          return { ...accountNoDisplayMap[addAcc.accountNoDisplay], ...addAcc };
        }
        return addAcc;
      });

      // Now merge accounts with these merged additional accounts, by accountNo as before
      const mergedAccounts = unionBy(accounts, mergedAdditionalAccounts, "accountNo");
      return mergedAccounts;
    }
  ),
  accountOptions: createSelector(
    (state) => CustomerSelector.accounts(state),
    (accounts) => mapAccountsToSelectOptions(accounts)
  ),
  paymentAccounts: createSelector([(state) => CustomerSelector.accounts(state)], (accounts) =>
    accounts.filter((account) => account.payment)
  ),
  paymentAccountOptions: createSelector(
    (state) => CustomerSelector.paymentAccounts(state),
    (accounts) => mapAccountsToSelectOptions(accounts)
  ),
  receiveAccounts: createSelector([(state) => CustomerSelector.accounts(state)], (accounts) =>
    accounts.filter((account) => account.receive)
  ),
  receiveAccountOptions: createSelector(
    (state) => CustomerSelector.receiveAccounts(state),
    (accounts) => mapAccountsToSelectOptions(accounts)
  ),
  //* Global Account usually use at Secondary Market
  globalAccounts: createSelector(
    [
      (state) => CustomerSelector.state(state).globalAccounts,
      (state) => CustomerSelector.state(state).globalAdditionalAccounts,
    ],
    (globalAccounts, globalAdditionalAccounts) => unionBy(globalAccounts, globalAdditionalAccounts, "accountNo")
  ),
  globalAccountOptions: createSelector(
    (state) => CustomerSelector.globalAccounts(state),
    (accounts) => mapAccountsToSelectOptions(accounts, "accountNo", "accountId")
  ),
  //* End Accounts
};
